import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [facility, setFacility] = useState(null);
  const [loading, setLoading] = useState(true);

  // Function to clear all auth data
  const clearAuthData = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('facility');
    api.setToken(null);
    setUser(null);
    setFacility(null);
  };

  useEffect(() => {
    // Check if user is logged in from token
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const userData = await api.getCurrentUser();
          
          // Validate we got proper user data
          if (!userData || !userData.email) {
            console.error('Invalid user data received');
            clearAuthData();
            setLoading(false);
            return;
          }
          
          setUser(userData);
          
          // Load facility data if user has a facility_id
          if (userData.facility_id) {
            try {
              const facilityData = await api.getFacility(userData.facility_id);
              setFacility(facilityData);
              localStorage.setItem('facility', JSON.stringify(facilityData));
            } catch (facilityError) {
              console.error('Error loading facility:', facilityError);
              // Don't fail auth just because facility load failed
            }
          }
        } catch (error) {
          console.error('Auth init error:', error);
          // Clear everything on auth failure to prevent loops
          clearAuthData();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    // Clear any existing auth data before attempting login
    clearAuthData();
    
    try {
      const response = await api.login(email, password);
      
      if (!response.user || !response.user.email) {
        throw new Error('Invalid response from server');
      }
      
      setUser(response.user);
      localStorage.setItem('user', JSON.stringify(response.user));
      
      // Load facility data if user has a facility_id
      if (response.user.facility_id) {
        try {
          const facilityData = await api.getFacility(response.user.facility_id);
          setFacility(facilityData);
          localStorage.setItem('facility', JSON.stringify(facilityData));
        } catch (facilityError) {
          console.error('Error loading facility:', facilityError);
          // Don't fail login just because facility load failed
        }
      }
      
      return { success: true, user: response.user, must_change_password: !!response.must_change_password };
    } catch (error) {
      // Ensure we're in a clean state after failed login
      clearAuthData();
      return { success: false, error: error.message || 'Invalid credentials' };
    }
  };

  const logout = () => {
    clearAuthData();
  };

  const refreshFacility = async () => {
    if (user?.facility_id) {
      try {
        const facilityData = await api.getFacility(user.facility_id);
        setFacility(facilityData);
        localStorage.setItem('facility', JSON.stringify(facilityData));
      } catch (error) {
        console.error('Error refreshing facility:', error);
      }
    }
  };

  const value = {
    user,
    facility,
    login,
    logout,
    loading,
    refreshFacility,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isSuperAdmin: user?.role === 'superadmin',
    isVenueManager: user?.role === 'venue_manager',
    isVenueOwner: user?.role === 'venue_owner',
    isVenueFinance: user?.role === 'venue_finance',
    // SEC-005: venue_owner + venue_finance are strictly read-only, single-venue.
    isReadOnlyRole: user?.role === 'venue_owner' || user?.role === 'venue_finance',
    isReadOnly: user?.role === 'venue_manager', // Venue managers have read-only access
    canEditBookings: user?.role !== 'venue_manager' && user?.role !== 'venue_owner' && user?.role !== 'venue_finance', // read-only roles cannot edit
    canCollectPayments: user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'venue_manager', // Venue managers can also collect payments via QR + payment links
    isStaff: user?.role === 'staff' || user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'venue_manager'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
